import React, { useCallback, useMemo } from 'react';
import { ResponsiveSVG } from 'components/ResponsiveSVG/ResponsiveSVG';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useRoutesState } from 'features/navbar/hooks/useRoutesState';
import { NavbarItem, TabItem } from 'features/navbar/types';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useCobrandImages } from 'features/cobrand/hooks/useCobrandImage';
import { useSelectNavigationTab } from 'features/navbar/hooks/useSelectNavigationTab';
import { useChatMessageData } from 'features/chat/hooks/useChatMessageData';
import { useInstructionsData } from 'features/chat/hooks/useInstructionsData';
import { tabletMobileNavbarItems, desktopNavbarItems } from 'features/navbar/navbarItems';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { MobileNavbar } from './MobileNavbar';
import { DesktopTabletNavbar } from './DesktopTabletNavbar';

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  useRoutesState();
  const navigationTab = useSelectNavigationTab();
  const { isDesktop, isMobile, isTablet } = useDeviceType();
  const { logo } = useCobrandImages();
  const { isEnabled } = useFeatureFlags();

  const { hasUnreadMessages } = useChatMessageData();
  const { hasUnreadInstructions } = useInstructionsData();

  const brandLogo = (
    <ResponsiveSVG
      svg={logo}
      containerStyle={{ overflow: 'hidden' }}
      deviceWidth={{ desktop: '170px', tablet: '160px', mobile: '150px' }}
      alt=""
      ariaHidden
    />
  );

  const handleTabChange = (tabObject: TabItem) => {
    // while navigating update of store is done too by useRoutesState
    navigate(tabObject?.route);
  };

  const onBrandLogoClick = () => {
    navigate('/');
  };

  const showChatBadge = useMemo(
    () => hasUnreadMessages || hasUnreadInstructions,
    [hasUnreadMessages, hasUnreadInstructions],
  );

  const enabledTabItemList = useCallback(
    (items: TabItem[]) =>
      items.filter(
        (item) =>
          (item.tabValue !== NavbarItem.PROGRESS || isEnabled(FeatureFlagKeys.PROGRESS_FEATURE)) &&
          (item.tabValue !== NavbarItem.PROFILE || isEnabled(FeatureFlagKeys.PROFILE_FEATURE)),
      ),
    [isEnabled],
  );

  const desktopNavItemList = useMemo(
    () => enabledTabItemList(desktopNavbarItems),
    [enabledTabItemList],
  );

  const tabletMobileNavItemList = useMemo(
    () =>
      enabledTabItemList(tabletMobileNavbarItems).map((item) => ({
        ...item,
        showBadge: item.tabValue === NavbarItem.CHAT ? showChatBadge : false,
      })),
    [enabledTabItemList, showChatBadge],
  );

  return (
    <>
      {(isDesktop || isTablet) && (
        <DesktopTabletNavbar
          onBrandLogoClick={onBrandLogoClick}
          onTabChange={handleTabChange}
          selectedTab={navigationTab?.selectedTabValue}
          brandLogo={brandLogo}
          desktopNavitemList={desktopNavItemList}
          tabletNavitemList={tabletMobileNavItemList}
        />
      )}
      {isMobile && (
        <MobileNavbar
          onBrandLogoClick={onBrandLogoClick}
          selectedTab={navigationTab?.selectedTabValue}
          onTabChange={handleTabChange}
          brandLogo={brandLogo}
          navItemList={tabletMobileNavItemList}
        />
      )}
    </>
  );
};
